import React from 'react';
import { detectMediaType, getEmbedSrc } from '../lib/mediaEmbed';
import { ExternalLink } from 'lucide-react';

// Renders the appropriate embed for a media URL.
// - YouTube / Vimeo: iframe player
// - mp4/webm/ogg/mov: <video controls>
// - image: <img>
// - other link: outbound link button
// Falls back to null when no URL is provided (caller can show its own placeholder).
export default function MediaEmbed({ url, title, className = '' }) {
  const type = detectMediaType(url);
  if (!type) return null;

  if (type === 'youtube' || type === 'vimeo') {
    const src = getEmbedSrc(url);
    if (!src) return null;
    return (
      <div
        className={`relative w-full overflow-hidden rounded-2xl bg-ink/5 ${className}`}
        style={{ paddingTop: '56.25%' }}
        data-testid="media-embed-iframe"
      >
        <iframe
          src={src}
          title={title || 'media'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  if (type === 'video') {
    return (
      <video
        controls
        src={url}
        className={`w-full rounded-2xl bg-black ${className}`}
        data-testid="media-embed-video"
      />
    );
  }

  if (type === 'image') {
    return (
      <img
        src={url}
        alt={title || ''}
        className={`w-full rounded-2xl object-cover ${className}`}
        data-testid="media-embed-image"
      />
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-sm text-terracotta hover:underline"
      data-testid="media-embed-link"
    >
      <ExternalLink size={14} /> {url}
    </a>
  );
}
